import IconComponent from "@mdi/react";
import { toast } from "react-toastify";

import { mdiContentCopy } from "@mdi/js";
import styled from "styled-components";

import { WellDetailCardData } from "api/data";

import { Tooltip } from "components/base";

import { PanelHeaderField } from "./PanelHeaderField";
import { reportedName } from "./constants";
import { MISSING_VALUE, TEAL } from "./styles";

export interface WellCardPanelHeaderProps {
  formattedUwi: Maybe<string>;
  rawUwi: Maybe<string>;
  wellData: Maybe<WellDetailCardData>;
  /** The values still describe the previous target. */
  stale?: boolean;
}

export function WellCardPanelHeader({
  formattedUwi,
  rawUwi,
  wellData,
  stale = false
}: WellCardPanelHeaderProps): JSX.Element {
  // The well name where a source gives one, and the entity name, which is the
  // uwi for most sources, where it does not.
  const name =
    reportedName(wellData?.["Header.WellName"], wellData?.["Header.EntityName"]) ??
    formattedUwi;

  const uwi = formattedUwi ?? rawUwi;

  return (
    <Wrapper $stale={stale} data-testid="well-card-panel-header">
      <WellName>
        {name ?? MISSING_VALUE}
        <CopyValueButton value={name} label="well name" testId="well-card-copy-name" />
      </WellName>
      <Fields>
        <PanelHeaderField label="API#/UWI" value={uwi}>
          <CopyValueButton value={uwi} label="API#/UWI" testId="well-card-copy-uwi" />
        </PanelHeaderField>
        <PanelHeaderField
          label="Operator"
          propertyName="Header.Operator_Short"
          wellData={wellData}
        />
        <PanelHeaderField
          label="Well Type"
          propertyName="Header.WellType"
          wellData={wellData}
        />
        <PanelHeaderField
          label="Status"
          propertyName="Header.Status"
          wellData={wellData}
        />
        <PanelHeaderField label="Zone" propertyName="Geology.Zone" wellData={wellData} />
      </Fields>
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $stale: boolean }>`
  padding: 10px 14px 9px;
  border-bottom: 1px solid #e3e6e8;
  flex: none;
  opacity: ${(props) => (props.$stale ? 0.4 : 1)};
  transition: opacity 120ms linear;
`;

interface CopyValueButtonProps {
  value: Maybe<string>;
  label: string;
  testId: string;
}

function CopyValueButton({ value, label, testId }: CopyValueButtonProps): JSX.Element {
  if (!value) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copied ${label} to clipboard`);
    } catch {
      toast.error(`Could not copy the ${label} to the clipboard`);
    }
  };

  return (
    <Tooltip title={`Copy ${label}`}>
      <CopyButton onClick={copy} aria-label={`Copy ${label}`} data-testid={testId}>
        <IconComponent path={mdiContentCopy} size={COPY_ICON_SIZE} />
      </CopyButton>
    </Tooltip>
  );
}

const WellName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${TEAL};
  font-size: 18px;
  font-weight: 800;
`;

// In em so the icon keeps its proportion to both the 18px name and the 15px
// value: just under the text height, so it never reads as the larger of the two.
const COPY_ICON_SIZE = "0.94em";

const CopyButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  font-size: inherit;
  color: var(--color-icon);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.33em;
  height: 1.33em;
  flex: none;
  transition: color var(--duration);

  &:hover {
    color: ${TEAL};
  }
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 36px;
  row-gap: 8px;
  margin-top: 6px;
`;
